<template>
  <div class="rt-shell">
    <!-- Toast -->
    <transition name="toast">
      <div
        v-if="toast.message"
        class="rt-toast"
        :class="'is-' + (toast.type || 'info')"
      >
        <span class="dot"></span>
        <span>{{ toast.message }}</span>
      </div>
    </transition>

    <!-- Header -->
    <header class="rt-header">
      <div>
        <div class="eyebrow">Superadmin</div>
        <h1>Routing</h1>
        <p class="subtitle">Client → branch routing groups. Direct which branches serve which clients.</p>
      </div>
      <div class="header-actions">
        <button
          type="button"
          class="btn btn-ghost"
          @click="reloadCache"
          title="Force reload the in-memory routing cache (rare escape hatch)"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="btn-icon">
            <path d="M20 12a8 8 0 1 1-2.34-5.66" stroke-linecap="round" />
            <path d="M20 4v5h-5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          Reload cache
        </button>
      </div>
    </header>

    <!-- Tabs -->
    <nav class="rt-tabs">
      <button
        v-for="t in tabs"
        :key="t.key"
        type="button"
        class="rt-tab"
        :class="{ 'rt-tab-active': tab === t.key }"
        @click="tab = t.key"
      >
        <span class="rt-tab-label">{{ t.label }}</span>
      </button>
    </nav>

    <!-- ==================== Tab 1: Groups ==================== -->
    <template v-if="tab === 'groups'">
      <!-- Create group -->
      <section class="rt-card">
        <div class="card-head">
          <div class="card-title">New Routing Group</div>
          <div class="card-sub">Create a group that binds specific branches to specific clients.</div>
        </div>
        <div class="new-group-grid">
          <div class="rt-field">
            <label>Vendor</label>
            <input v-model="newGroup.vendor" type="text" placeholder="vendor username" class="rt-input" />
          </div>
          <div class="rt-field">
            <label>Group name</label>
            <input v-model="newGroup.name" type="text" placeholder="e.g. north-region" class="rt-input" />
          </div>
          <div class="rt-field">
            <label>Order type</label>
            <select v-model="newGroup.order_type" class="rt-input">
              <option value="both">Both</option>
              <option value="payin">Pay-In</option>
              <option value="payout">Pay-Out</option>
            </select>
          </div>
          <div class="rt-field rt-field-btn">
            <button
              type="button"
              class="btn btn-primary"
              :disabled="isBusy"
              @click="createGroup"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" class="btn-icon">
                <path d="M12 5v14M5 12h14" stroke-linecap="round" />
              </svg>
              Create group
            </button>
          </div>
        </div>
      </section>

      <!-- Existing groups -->
      <section class="rt-card">
        <div class="card-head">
          <div class="card-title">
            Existing Groups
            <span class="head-count">{{ groups.length }}</span>
          </div>
        </div>

        <div v-if="isLoading" class="rt-loading">
          <div class="loading-spinner"></div>
          <div class="loading-label">Loading…</div>
        </div>

        <div v-else-if="!groups.length" class="empty-mini">
          No groups yet. Create one above.
        </div>

        <div v-else class="table-wrap">
          <table class="rt-table">
            <thead>
              <tr>
                <th>Vendor</th>
                <th>Name</th>
                <th>Order type</th>
                <th class="th-num">Branches</th>
                <th class="th-num">Clients</th>
                <th>Created by</th>
                <th class="th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="g in groups" :key="g.id" :class="{ 'row-editing': editingGroupId === g.id }">
                <td class="td-mono">{{ g.vendor }}</td>
                <td>
                  <input
                    v-if="editingGroupId === g.id"
                    v-model="editingGroup.name"
                    class="rt-input rt-input-inline"
                  />
                  <span v-else class="td-strong">{{ g.name }}</span>
                </td>
                <td>
                  <select
                    v-if="editingGroupId === g.id"
                    v-model="editingGroup.order_type"
                    class="rt-input rt-input-inline"
                  >
                    <option value="both">Both</option>
                    <option value="payin">Pay-In</option>
                    <option value="payout">Pay-Out</option>
                  </select>
                  <span v-else class="pill" :class="'pill-type-' + g.order_type">{{ g.order_type }}</span>
                </td>
                <td class="td-num">{{ g.branch_count }}</td>
                <td class="td-num">{{ g.client_count }}</td>
                <td class="td-mini">{{ g.created_by }}</td>
                <td class="td-actions">
                  <template v-if="editingGroupId === g.id">
                    <button type="button" class="action-btn action-success" @click="saveGroup(g.id)">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" /></svg>
                      Save
                    </button>
                    <button type="button" class="action-btn" @click="editingGroupId = null">Cancel</button>
                  </template>
                  <template v-else>
                    <button type="button" class="action-btn" @click="editGroup(g)" title="Edit">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 20h9" stroke-linecap="round" /><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" stroke-linecap="round" stroke-linejoin="round" /></svg>
                      Edit
                    </button>
                    <button type="button" class="action-btn action-primary" @click="openMembersFor(g)" title="Manage branches in this group">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke-linecap="round" stroke-linejoin="round" /></svg>
                      Members
                    </button>
                    <button type="button" class="action-btn action-danger" @click="deleteGroup(g)" title="Delete">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" stroke-linecap="round" stroke-linejoin="round" /></svg>
                    </button>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>

    <!-- ==================== Tab 2: Group members ==================== -->
    <template v-if="tab === 'members'">
      <section class="rt-card">
        <div class="card-head">
          <div class="card-title">Group Selection</div>
          <div class="card-sub">Pick a routing group to edit which branches belong to it.</div>
        </div>
        <div class="rt-field">
          <label>Group</label>
          <select v-model="selectedGroupId" @change="loadMembers" class="rt-input">
            <option :value="null">— pick a group —</option>
            <option v-for="g in groups" :key="g.id" :value="g.id">
              {{ g.vendor }} / {{ g.name }} ({{ g.order_type }})
            </option>
          </select>
        </div>
      </section>

      <!-- Orphaned-branches warning -->
      <div v-if="orphaned.length" class="callout callout-danger">
        <div class="callout-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 9v4M12 17h.01" stroke-linecap="round" />
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
        </div>
        <div class="callout-body">
          <div class="callout-title">
            {{ orphaned.length }} exclusive branch(es) not in any group
          </div>
          <div class="callout-sub">
            These branches are <b>exclusive to routing groups</b> for the direction shown but belong to no group — for that direction they can't route any orders. Either add them to a group below, or turn off that direction's exclusivity on the branch edit form.
          </div>
          <ul class="orphan-list">
            <li v-for="b in orphaned" :key="b.id">
              <span class="orphan-vendor">{{ b.vendor }}</span>
              <span class="orphan-slash">/</span>
              <strong class="orphan-user">{{ b.username }}</strong>
              <span class="orphan-name">{{ b.name }}</span>
              <span v-if="exclusiveLabel(b)" class="pill pill-exclusive">{{ exclusiveLabel(b) }}</span>
            </li>
          </ul>
        </div>
      </div>

      <div v-if="selectedGroupId && members" class="dual-list">
        <!-- Available -->
        <section class="rt-card panel-panel">
          <div class="panel-head">
            <div class="panel-title">Available branches</div>
            <span class="head-count">{{ nonMemberCandidates.length }}</span>
          </div>
          <div class="panel-list">
            <div
              v-for="b in nonMemberCandidates"
              :key="b.id"
              class="panel-item panel-item-add"
              @click="addMember(b)"
            >
              <div class="pi-body">
                <div class="pi-title">
                  {{ b.username }}
                  <span
                    v-if="exclusiveLabel(b)"
                    class="pill pill-exclusive"
                    :title="`Exclusive (${exclusiveLabel(b)}) — for that direction this branch only serves clients in a routing group that includes it.`"
                  >EXCLUSIVE · {{ exclusiveLabel(b) }}</span>
                </div>
                <div class="pi-sub">{{ b.name }}</div>
              </div>
              <div class="pi-add">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                  <path d="M12 5v14M5 12h14" stroke-linecap="round" />
                </svg>
              </div>
            </div>
            <div v-if="!nonMemberCandidates.length" class="panel-empty">
              All vendor branches are in this group.
            </div>
          </div>
        </section>

        <!-- In group -->
        <section class="rt-card panel-panel">
          <div class="panel-head">
            <div>
              <div class="panel-title">In group</div>
              <span class="head-count">{{ memberList.length }}</span>
            </div>
            <button
              type="button"
              class="btn btn-success btn-sm"
              :disabled="isBusy"
              @click="saveMembers"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" class="btn-icon">
                <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              Save members
            </button>
          </div>
          <div class="panel-list">
            <div
              v-for="b in memberList"
              :key="b.id"
              class="panel-item panel-item-remove"
              @click="removeMember(b)"
            >
              <div class="pi-body">
                <div class="pi-title">
                  {{ b.username }}
                  <span
                    v-if="exclusiveLabel(b)"
                    class="pill pill-exclusive"
                    :title="`Exclusive (${exclusiveLabel(b)}) — for that direction this branch only serves clients in a routing group that includes it.`"
                  >EXCLUSIVE · {{ exclusiveLabel(b) }}</span>
                </div>
                <div class="pi-sub">{{ b.name }}</div>
              </div>
              <div class="pi-remove">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                  <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
                </svg>
              </div>
            </div>
            <div v-if="!memberList.length" class="panel-empty">
              No branches in group yet. Click one on the left to add.
            </div>
          </div>
        </section>
      </div>

      <div v-else-if="!selectedGroupId" class="empty-hero">
        <div class="empty-hero-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
            <path d="M22 11.08V12a10 10 0 11-5.93-9.14" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M22 4L12 14.01l-3-3" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>
        <div class="empty-hero-title">Pick a group above</div>
        <div class="empty-hero-sub">Then drag branches between the two lists to change membership.</div>
      </div>
    </template>

    <!-- ==================== Tab 3: Client assignment ==================== -->
    <template v-if="tab === 'clients'">
      <section class="rt-card">
        <div class="card-head">
          <div class="card-title">
            Client Assignment
            <span class="head-count">{{ clients.length }}</span>
          </div>
          <div class="card-sub">Assign each client to a routing group, or leave on the default routing.</div>
        </div>

        <div v-if="isLoading" class="rt-loading">
          <div class="loading-spinner"></div>
          <div class="loading-label">Loading…</div>
        </div>

        <div v-else-if="!clients.length" class="empty-mini">No clients found.</div>

        <div v-else class="table-wrap">
          <table class="rt-table">
            <thead>
              <tr>
                <th>Vendor</th>
                <th>Client</th>
                <th>Username</th>
                <th>Current group</th>
                <th>Assign to</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in clients" :key="c.id">
                <td class="td-mono">{{ c.vendor }}</td>
                <td class="td-strong">{{ c.name }}</td>
                <td class="td-mini">{{ c.username }}</td>
                <td>
                  <template v-if="c.routing_group_id">
                    <span class="pill pill-group">{{ c.routing_group_name }}</span>
                    <span class="type-tag">{{ c.routing_group_order_type }}</span>
                  </template>
                  <span v-else class="pill pill-default">Default</span>
                </td>
                <td>
                  <select
                    :value="c.routing_group_id || ''"
                    @change="assignClient(c, $event.target.value)"
                    class="rt-input rt-input-inline"
                  >
                    <option value="">— default —</option>
                    <option
                      v-for="g in groupsForVendor(c.vendor)"
                      :key="g.id"
                      :value="g.id"
                    >{{ g.name }} ({{ g.order_type }})</option>
                  </select>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import http from "../../http-common";

export default {
  name: "routing",
  data() {
    return {
      tab: "groups",
      tabs: [
        { key: "groups", label: "Groups" },
        { key: "members", label: "Group members" },
        { key: "clients", label: "Client assignment" },
      ],
      isLoading: false,
      isBusy: false,
      toast: { type: "", message: "" },

      // Tab 1: groups
      groups: [],
      newGroup: { vendor: "", name: "", order_type: "both" },
      editingGroupId: null,
      editingGroup: { name: "", order_type: "both" },

      // Tab 2: members
      selectedGroupId: null,
      members: null, // { group, members, candidates }
      memberList: [], // ids currently in group (mutable)

      // Tab 3: clients
      clients: [],

      // Exclusive branches (branch_serves_default=0) that aren't in
      // any routing group. Rendered as a warning banner on the Members
      // tab. Refreshed alongside groups on load + after any membership
      // change so the count is always current.
      orphaned: [],
    };
  },
  computed: {
    nonMemberCandidates() {
      if (!this.members) return [];
      const inSet = new Set(this.memberList.map((b) => b.id));
      return this.members.candidates.filter((b) => !inSet.has(b.id));
    },
  },
  async created() {
    await this.loadGroups();
    await this.loadClients();
    await this.loadOrphaned();
  },
  methods: {
    // Human label for a branch's per-direction exclusivity: 'PAY-IN',
    // 'PAY-OUT', 'BOTH', or null when the branch is fully open. Falls
    // back to the legacy direction-blind flag for API rows served
    // before the 22072026 migration.
    exclusiveLabel(b) {
      const legacy = b.branch_serves_default != null && Number(b.branch_serves_default) === 0;
      const payin = b.branch_serves_default_payin != null
        ? Number(b.branch_serves_default_payin) === 0
        : legacy;
      const payout = b.branch_serves_default_payout != null
        ? Number(b.branch_serves_default_payout) === 0
        : legacy;
      if (payin && payout) return 'BOTH';
      if (payin) return 'PAY-IN';
      if (payout) return 'PAY-OUT';
      return null;
    },
    showToast(type, message) {
      this.toast = { type, message };
      setTimeout(() => { if (this.toast.message === message) this.toast = { type: "", message: "" }; }, 2400);
    },
    async loadGroups() {
      this.isLoading = true;
      try {
        const { data } = await http.get("/routing/groups");
        this.groups = data.data || [];
      } catch (e) {
        this.showToast("error", e?.response?.data?.message || e.message);
      } finally {
        this.isLoading = false;
      }
    },
    async createGroup() {
      if (!this.newGroup.vendor || !this.newGroup.name) {
        this.showToast("error", "Vendor and name are required.");
        return;
      }
      this.isBusy = true;
      try {
        await http.post("/routing/groups", { ...this.newGroup });
        this.newGroup = { vendor: "", name: "", order_type: "both" };
        await this.loadGroups();
        this.showToast("success", "Group created.");
      } catch (e) {
        this.showToast("error", e?.response?.data?.message || e.message);
      } finally {
        this.isBusy = false;
      }
    },
    editGroup(g) {
      this.editingGroupId = g.id;
      this.editingGroup = { name: g.name, order_type: g.order_type };
    },
    async saveGroup(id) {
      this.isBusy = true;
      try {
        await http.put(`/routing/groups/${id}`, { ...this.editingGroup });
        this.editingGroupId = null;
        await this.loadGroups();
        this.showToast("success", "Group updated.");
      } catch (e) {
        this.showToast("error", e?.response?.data?.message || e.message);
      } finally {
        this.isBusy = false;
      }
    },
    async deleteGroup(g) {
      if (!confirm(`Delete group "${g.name}"?`)) return;
      this.isBusy = true;
      try {
        await http.delete(`/routing/groups/${g.id}`);
        await this.loadGroups();
        this.showToast("success", "Group deleted.");
      } catch (e) {
        this.showToast("error", e?.response?.data?.message || e.message);
      } finally {
        this.isBusy = false;
      }
    },
    openMembersFor(g) {
      this.selectedGroupId = g.id;
      this.tab = "members";
      this.loadMembers();
    },
    async loadMembers() {
      if (!this.selectedGroupId) {
        this.members = null;
        this.memberList = [];
        return;
      }
      this.isBusy = true;
      try {
        const { data } = await http.get(`/routing/groups/${this.selectedGroupId}/branches`);
        this.members = data;
        this.memberList = [...(data.members || [])];
      } catch (e) {
        this.showToast("error", e?.response?.data?.message || e.message);
      } finally {
        this.isBusy = false;
      }
    },
    addMember(b) {
      if (this.memberList.find((x) => x.id === b.id)) return;
      this.memberList.push(b);
    },
    removeMember(b) {
      this.memberList = this.memberList.filter((x) => x.id !== b.id);
    },
    async saveMembers() {
      if (!this.selectedGroupId) return;
      this.isBusy = true;
      try {
        await http.put(`/routing/groups/${this.selectedGroupId}/branches`, {
          branch_ids: this.memberList.map((b) => b.id),
        });
        await this.loadGroups(); // refresh branch_count
        await this.loadOrphaned(); // membership change may have cleared/created orphans
        this.showToast("success", "Members saved.");
      } catch (e) {
        this.showToast("error", e?.response?.data?.message || e.message);
      } finally {
        this.isBusy = false;
      }
    },
    async loadOrphaned() {
      try {
        const { data } = await http.get("/routing/branches/orphaned");
        this.orphaned = data.data || [];
      } catch (e) {
        // Non-fatal — orphaned list is a warning, not a blocker.
        this.orphaned = [];
      }
    },
    async loadClients() {
      try {
        const { data } = await http.get("/routing/clients");
        this.clients = data.data || [];
      } catch (e) {
        this.showToast("error", e?.response?.data?.message || e.message);
      }
    },
    groupsForVendor(vendor) {
      return this.groups.filter((g) => g.vendor === vendor);
    },
    async assignClient(client, groupId) {
      const routing_group_id = groupId === "" ? null : Number(groupId);
      try {
        await http.post(`/routing/clients/${client.id}/group`, { routing_group_id });
        client.routing_group_id = routing_group_id;
        if (routing_group_id) {
          const g = this.groups.find((x) => x.id === routing_group_id);
          if (g) {
            client.routing_group_name = g.name;
            client.routing_group_order_type = g.order_type;
          }
        } else {
          client.routing_group_name = null;
          client.routing_group_order_type = null;
        }
        await this.loadGroups(); // refresh client_count on the affected group
        this.showToast("success", `Assigned ${client.name}.`);
      } catch (e) {
        this.showToast("error", e?.response?.data?.message || e.message);
      }
    },
    async reloadCache() {
      try {
        await http.post("/routing/cache/reload", {});
        this.showToast("success", "Cache reloaded.");
      } catch (e) {
        this.showToast("error", e?.response?.data?.message || e.message);
      }
    },
  },
};
</script>

<style scoped>
/* ==================== Shell ==================== */
.rt-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 18px;
  background: linear-gradient(180deg, #fbfaf7 0%, #f4f0e5 100%);
  overflow-y: auto;
}

/* ==================== Header ==================== */
.rt-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #1B2A47;
  font-weight: 600;
  margin-bottom: 4px;
}
.rt-header h1 {
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.1;
  margin: 0;
}
.subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0;
  max-width: 640px;
}

/* ==================== Buttons ==================== */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  outline: none;
}
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-sm { padding: 6px 12px; font-size: 12px; }
.btn-primary {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 6px 16px -6px rgba(27, 42, 71, 0.5);
}
.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px -8px rgba(27, 42, 71, 0.55);
}
.btn-success {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: #fff;
  box-shadow: 0 6px 16px -6px rgba(5, 150, 105, 0.5);
}
.btn-success:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px -8px rgba(5, 150, 105, 0.55);
}
.btn-ghost {
  background: #fff;
  color: #475569;
  border-color: #e2e8f0;
}
.btn-ghost:hover:not(:disabled) { background: #f8fafc; border-color: #cbd5e1; }
.btn-icon { width: 15px; height: 15px; }

/* ==================== Tabs ==================== */
.rt-tabs {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  align-self: flex-start;
  flex-wrap: wrap;
}
.rt-tab {
  border: none;
  background: transparent;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.rt-tab:hover { color: #1B2A47; background: #f8fafc; }
.rt-tab-active,
.rt-tab-active:hover {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 4px 10px -4px rgba(27, 42, 71, 0.45);
}

/* ==================== Card ==================== */
.rt-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.card-head {}
.card-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.card-sub {
  font-size: 12px;
  color: #64748b;
  margin-top: 3px;
}
.head-count {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 999px;
}

/* ==================== Fields ==================== */
.rt-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.rt-field label {
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.rt-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #f8fafc;
  outline: none;
  color: #0f172a;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  font-family: inherit;
}
.rt-input::placeholder { color: #94a3b8; }
.rt-input:focus {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(27, 42, 71, 0.14);
}
.rt-input-inline {
  padding: 6px 10px;
  font-size: 12px;
}
select.rt-input {
  appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' d='M1 1l5 5 5-5'/></svg>");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 32px;
}

.new-group-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: 12px 14px;
  align-items: end;
}
.rt-field-btn { align-self: end; }
@media (max-width: 720px) {
  .new-group-grid { grid-template-columns: 1fr 1fr; }
  .rt-field-btn { grid-column: 1 / -1; }
}

/* ==================== Loading + empty ==================== */
.rt-loading {
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
}
.loading-spinner {
  width: 36px; height: 36px;
  border-radius: 999px;
  border: 3px solid #e2e8f0;
  border-top-color: #1B2A47;
  animation: spin 0.9s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.loading-label { font-size: 13px; color: #64748b; }

.empty-mini {
  padding: 24px 20px;
  text-align: center;
  font-size: 13px;
  color: #94a3b8;
  border: 1px dashed #e2e8f0;
  border-radius: 12px;
}
.empty-hero {
  background: #fff;
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  padding: 40px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
}
.empty-hero-icon {
  width: 52px; height: 52px;
  border-radius: 14px;
  background: #eef2f7;
  color: #1B2A47;
  display: flex; align-items: center; justify-content: center;
}
.empty-hero-icon svg { width: 26px; height: 26px; }
.empty-hero-title { font-size: 15px; font-weight: 600; color: #0f172a; }
.empty-hero-sub { font-size: 12px; color: #64748b; }

/* ==================== Table ==================== */
.table-wrap {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  overflow-x: auto;
}
.rt-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.rt-table thead {
  background: #f8fafc;
}
.rt-table th {
  padding: 10px 12px;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}
.th-num { text-align: right; }
.th-actions { text-align: right; }
.rt-table tbody tr {
  transition: background 0.12s;
}
.rt-table tbody tr:nth-child(even) { background: #fafbfc; }
.rt-table tbody tr:hover { background: #eef2f7; }
.rt-table tbody tr.row-editing { background: #fffbeb; }
.rt-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f1f5f9;
  color: #0f172a;
  vertical-align: middle;
}
.rt-table tbody tr:last-child td { border-bottom: none; }
.td-num { text-align: right; font-variant-numeric: tabular-nums; }
.td-strong { font-weight: 600; }
.td-mono { font-family: 'SFMono-Regular', Menlo, monospace; font-size: 11px; color: #475569; }
.td-mini { font-size: 11px; color: #64748b; }
.td-actions {
  text-align: right;
  white-space: nowrap;
  display: flex;
  gap: 4px;
  justify-content: flex-end;
}

/* Action buttons in table rows */
.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 7px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  color: #475569;
  transition: all 0.12s;
}
.action-btn svg { width: 12px; height: 12px; }
.action-btn:hover { border-color: #cbd5e1; background: #f8fafc; }
.action-primary { color: #1B2A47; border-color: #cbd5e1; background: #eef2f7; }
.action-primary:hover { background: #e0e7ff; border-color: #94a3b8; }
.action-success { color: #047857; border-color: #a7f3d0; background: #ecfdf5; }
.action-success:hover { background: #d1fae5; }
.action-danger { color: #b91c1c; border-color: #fecaca; background: #fef2f2; }
.action-danger:hover { background: #fee2e2; }

/* ==================== Pills ==================== */
.pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.pill-type-both   { background: #eef2f7; color: #1B2A47; }
.pill-type-payin  { background: #ecfdf5; color: #047857; }
.pill-type-payout { background: #fef3c7; color: #92400e; }
.pill-exclusive   { background: #fee2e2; color: #b91c1c; font-size: 10px; padding: 1px 7px; margin-left: 6px; }
.pill-group       { background: #eef2f7; color: #1B2A47; }
.pill-default     { background: #f1f5f9; color: #64748b; }
.type-tag {
  font-size: 10px;
  color: #94a3b8;
  margin-left: 6px;
  font-family: 'SFMono-Regular', Menlo, monospace;
}

/* ==================== Callout ==================== */
.callout {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid transparent;
}
.callout-danger {
  background: #fef2f2;
  border-color: #fecaca;
  color: #991b1b;
}
.callout-icon {
  width: 32px; height: 32px;
  border-radius: 10px;
  background: rgba(220, 38, 38, 0.12);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #dc2626;
  flex-shrink: 0;
}
.callout-icon svg { width: 16px; height: 16px; }
.callout-body { flex: 1; min-width: 0; }
.callout-title {
  font-size: 13px;
  font-weight: 700;
  color: #991b1b;
}
.callout-sub {
  font-size: 12px;
  color: #7f1d1d;
  margin-top: 4px;
  line-height: 1.45;
}
.orphan-list {
  list-style: none;
  padding: 0;
  margin: 10px 0 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.orphan-list li {
  font-size: 12px;
  color: #7f1d1d;
  padding: 4px 8px;
  background: rgba(255,255,255,0.5);
  border-radius: 6px;
}
.orphan-vendor { font-family: 'SFMono-Regular', Menlo, monospace; color: #991b1b; font-size: 11px; }
.orphan-slash { color: #cbd5e1; margin: 0 4px; }
.orphan-user { color: #7f1d1d; }
.orphan-name { color: #b45309; font-size: 11px; margin-left: 6px; }

/* ==================== Dual list (members tab) ==================== */
.dual-list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
@media (max-width: 900px) { .dual-list { grid-template-columns: 1fr; } }
.panel-panel { padding: 16px; gap: 12px; }
.panel-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}
.panel-title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.panel-list {
  border: 1px solid #eef2f7;
  border-radius: 10px;
  max-height: 480px;
  overflow-y: auto;
  background: #f8fafc;
}
.panel-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid #eef2f7;
  cursor: pointer;
  transition: background 0.12s;
  background: #fff;
}
.panel-item:last-child { border-bottom: none; }
.panel-item-add:hover { background: #eef2f7; }
.panel-item-remove:hover { background: #fef2f2; }
.pi-body { min-width: 0; }
.pi-title {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.pi-sub {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
}
.pi-add, .pi-remove {
  width: 26px; height: 26px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.12s, background 0.12s;
}
.panel-item:hover .pi-add,
.panel-item:hover .pi-remove { opacity: 1; }
.pi-add    { background: #eef2f7; color: #1B2A47; }
.pi-remove { background: #fef2f2; color: #dc2626; }
.pi-add svg, .pi-remove svg { width: 14px; height: 14px; }
.panel-empty {
  padding: 24px 12px;
  text-align: center;
  font-size: 12px;
  color: #94a3b8;
  background: #f8fafc;
}

/* ==================== Toast ==================== */
.rt-toast {
  position: fixed;
  top: 24px; right: 24px;
  z-index: 60;
  background: #0f172a; color: #fff;
  padding: 12px 18px;
  border-radius: 12px;
  font-size: 13px; font-weight: 500;
  display: inline-flex; align-items: center; gap: 10px;
  box-shadow: 0 12px 30px -10px rgba(15, 23, 42, 0.4);
}
.rt-toast .dot {
  width: 8px; height: 8px; border-radius: 999px; background: #94a3b8;
}
.rt-toast.is-success { background: #059669; }
.rt-toast.is-success .dot { background: #a7f3d0; }
.rt-toast.is-error { background: #dc2626; }
.rt-toast.is-error .dot { background: #fecaca; }

.toast-enter-active, .toast-leave-active { transition: all 0.24s cubic-bezier(0.4, 0, 0.2, 1); }
.toast-enter-from, .toast-leave-to { transform: translateY(-12px); opacity: 0; }
</style>
